import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { emailRegex, FormState, phoneRegex } from '@opu/utils';
import AxiosInstance from '../../../helpers/AxiosInstance';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

const useForm = ({ role }) => {
  let history = useHistory();
  const { t, i18n } = useTranslation();
  const [establishments, setEstablishments] = useState([]);
  const [loading, setLoading] = useState(false);

  const getEstablishments = () => {
    setLoading(true);
    AxiosInstance.get('/establishment')
      .then((res) => {
        setEstablishments(res.data.items);
      })
      .catch((error) => {
        Swal.fire(t('establishment.ERROR_GET_ESTABLISHMENT'), '', 'error');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const createStudent = async () => {
    console.log('🚀 Starting student creation process...');
    console.log('📝 Form data:', formState.inputValues);
    setLoading(true);
    
    try {
      // Format birth_date properly
      let formattedBirthDate = null;
      if (formState.inputValues.birth_date) {
        const date = new Date(formState.inputValues.birth_date);
        formattedBirthDate = date.toISOString().split('T')[0]; // YYYY-MM-DD format
        console.log('📅 Formatted birth date:', formattedBirthDate);
      }
      
      // Extract BAC year from matricule
      const bacYear = formState.inputValues.bac_matricule ? formState.inputValues.bac_matricule.substring(0, 4) : null;
      console.log('🎓 BAC year extracted:', bacYear);
      
      let userId;
      
      try {
        console.log('👤 Attempting to create new user...');
        const userData = {
          firstName: formState.inputValues.firstName,
          lastName: formState.inputValues.lastName,
          gender: formState.inputValues.gender,
          email: formState.inputValues.email || null,
          phone: formState.inputValues.phone,
          bac_year: bacYear,
          bac_matricule: formState.inputValues.bac_matricule,
          birth_date: formattedBirthDate,
        };
        console.log('📤 User data to send:', userData);
        
        // First try to create a new user
        const userResponse = await AxiosInstance.post('/student/user', userData);
        console.log('✅ User created successfully:', userResponse.data);
        userId = userResponse.data.id;
      } catch (userError) {
        console.log('❌ User creation failed:', userError);
        console.log('🔍 Error status:', userError.status);
        console.log('📄 Error message:', userError.data?.message);
        
        // If user creation fails due to duplicate BAC matricule, try to find the existing user
        if (userError.status === 400 && userError.data.message && userError.data.message.includes('BAC_MATRICULE_ALREADY_USED')) {
          console.log('🔄 BAC matricule already exists, trying to find existing user...');
          try {
            // Try to find the existing user by BAC matricule
            console.log('🔍 Searching for user with BAC matricule:', formState.inputValues.bac_matricule);
            const findUserResponse = await AxiosInstance.get(`/user/bac-matricule/${formState.inputValues.bac_matricule}`);
            console.log('✅ Found existing user:', findUserResponse.data);
            userId = findUserResponse.data.id;
          } catch (findError) {
            console.log('❌ Could not find existing user:', findError);
            // If we can't find the user, show an error
            Swal.fire(t('student.ERROR_BAC_MATRICULE_EXISTS'), '', 'error');
            return;
          }
        } else {
          // If it's a different error, re-throw it
          console.log('❌ Different error, re-throwing:', userError);
          throw userError;
        }
      }
      
      console.log('🆔 User ID to use for student creation:', userId);
      
      // Then create the student with the user ID
      const studentData = {
        userId: userId,
        level: formState.inputValues.level,
        isActivated: true,
        establishmentId: role === 'ESTABLISHMENT_USER' ? null : formState.inputValues.establishmentId,
      };
      console.log('📤 Student data to send:', studentData);
      
      const studentResponse = await AxiosInstance.post('/student', studentData);
      console.log('✅ Student created successfully:', studentResponse.data);
      
      Swal.fire(t('student.SUCCESS_CREATE_STUDENT'), '', 'success');
      resetFormState();
    } catch (error) {
      console.log('❌ Final error in student creation:', error);
      console.log('🔍 Error status:', error.status);
      console.log('📄 Error data:', error.data);
      
      if (error.status === 400) {
        handleErrorsServer(error.data.message);
      } else if (error.status === 401) {
        Swal.fire(t('ERROR_NOT_AUTHORIZED'), '', 'error');
      } else {
        Swal.fire(t('student.ERROR_CREATE_STUDENT'), '', 'error');
      }
    } finally {
      console.log('🏁 Student creation process finished');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (role !== 'ESTABLISHMENT_USER') {
      getEstablishments();
    }
  }, []);

  const inputValues = {
    firstName: { value: '', validate: { required: true } },
    lastName: { value: '', validate: { required: true } },
    email: { value: '', validate: { required: false, pattern: emailRegex } }, // Made optional
    bac_matricule: { value: '', validate: { required: true } }, // Made required
    birth_date: { value: '', validate: { required: true } }, // Added birth date
    phone: { value: '', validate: { required: false, pattern: phoneRegex } },
    gender: { value: '', validate: { required: false } },
    level: { value: '', validate: { required: true } },
    establishmentId: { value: '', validate: { required: role === 'ESTABLISHMENT_USER' ? false : true } },
  };

  const back = () => {
    history.goBack();
  };

  const { formState, submitHandler, resetFormState, field, handleErrorsServer } = FormState(inputValues, createStudent);

  return {
    submitHandler,
    resetFormState,
    loading,
    field,
    establishments,
    back,
  };
};

export default useForm;
